// noinspection DuplicatedCode
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Button from "@material-tailwind/react/Button";
import Modal from "@material-tailwind/react/Modal";
import ModalBody from "@material-tailwind/react/ModalBody";
import ModalFooter from "@material-tailwind/react/ModalFooter";
import ModalHeader from "@material-tailwind/react/ModalHeader";
import { toast } from "react-toastify";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";

// Import Chart.js dengan cara yang benar - HARUS DI ATAS
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";

// Import slice absensi - HARUS DI ATAS
import {
  getAbsensiTransaksi,
  loadMoreAbsensi,
  importAbsensiTransaksi,
  getAbsensiSummary,
  downloadTemplateAbsensi,
  clearAbsensiMessage,
  resetPagination,
  clearAbsensiData,
  absensiTransaksiSelector,
  getKaryawanSelect,
  karyawanSelectSelector,
} from "./absensiTransaksiSlice";

import { Colors } from "../../../../assets/Colors";
import Loading from "../../../../components/loading/Loading";

// Register ChartJS components - SETELAH IMPORT
// Skala kartesius (CategoryScale/LinearScale/BarElement) tak lagi didaftarkan:
// grafik batang keterlambatan sudah dilepas, tinggal doughnut kehadiran.
ChartJS.register(Title, Tooltip, Legend, ArcElement);

// Chart options configuration - SETELAH IMPORT
const doughnutChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom",
    },
    title: {
      display: true,
      text: "Kehadiran Karyawan",
      font: {
        size: 16,
      },
    },
  },
};

// Tanggal → "YYYY-MM-DD" memakai komponen LOKAL, bukan toISOString().
// toISOString() menerjemahkan ke UTC: DatePicker memberi 00:00 WIB, yang di UTC
// menjadi pukul 17:00 HARI SEBELUMNYA — memilih 30/07 akan mengirim 2026-07-29.
// Pada halaman per hari kesalahan itu langsung terlihat sebagai data salah hari.
// Label tampilan utk kategori aktif (klik kartu Rekap Kehadiran) — key harus
// PERSIS sama dengan yg dikirim ke backend (`kategori` di query), lihat
// `absensiKategoriWhere` di perbaikan.go.
const KATEGORI_LABEL = {
  hadir_bersih: "Hadir",
  hadir_lengkap: "Hadir Lengkap",
  tanpa_scan_pulang: "Tanpa Scan Pulang",
  cuti: "Cuti",
  sakit: "Sakit",
  izin: "Izin",
  tanpa_keterangan: "Tanpa Keterangan",
  terlambat: "Terlambat",
  lembur: "Lembur",
};

const toLocalDateParam = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const bulan = String(d.getMonth() + 1).padStart(2, "0");
  const tanggal = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${bulan}-${tanggal}`;
};

const AbsensiPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State untuk form dan UI.
  // Halaman ini memakai RENTANG tanggal (startDate..endDate); default = hari ini saja.
  // Bentuk state (dan parameter start_date/end_date yang dikirim ke API) sengaja
  // dipertahankan supaya getAbsensiSummary & loadMoreAbsensi tidak ikut berubah —
  // ringkasan pun otomatis ikut menghitung satu hari yang sama dengan tabelnya.
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
  });
  const [selectedKaryawan, setSelectedKaryawan] = useState("");
  // Filter dari klik kartu "Rekap Kehadiran" (mis. "hadir_lengkap", "cuti").
  // "" = tampilkan semua karyawan, tanpa filter kategori.
  const [kategoriFilter, setKategoriFilter] = useState("");
  const [showModalUpload, setShowModalUpload] = useState(false);
  const [excelFile, setExcelFile] = useState(null);

  // State untuk pagination
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const PAGE_SIZE = 50;

  // Selectors
  const absensiState = useSelector(absensiTransaksiSelector);
  const karyawanSelectState = useSelector(karyawanSelectSelector);

  // Destructure absensiState
  const {
    data: absensiData = [],
    summary: absensiSummary = {},
    pagination = {
      currentPage: 1,
      pageSize: PAGE_SIZE,
      totalCount: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false,
    },
    isGetFetching = false,
    isLoadMoreFetching = false,
    isImportFetching = false,
    isDownloadFetching = false,
    errorMessage: absensiErrorMessage = "",
    message: absensiMessage = "",
    importResult = null,
  } = absensiState;

  // Destructure karyawanSelectState
  const {
    karyawanOptions = [],
    isKaryawanSelectFetching = false,
    isKaryawanSelectError = false,
    errorMessage: karyawanErrorMessage = "",
  } = karyawanSelectState;

  // Calculate hasMoreData
  const hasMoreData = pagination.currentPage < pagination.totalPages;

  // Load data karyawan saat component mount
  useEffect(() => {
    const loadKaryawanData = async () => {
      try {
        await dispatch(getKaryawanSelect("")).unwrap();
      } catch (error) {
        if (!error.message?.includes("canceled")) {
          toast.error(`Gagal memuat data karyawan: ${error.message}`, {
            position: "bottom-right",
            autoClose: 5000,
          });
        }
      }
    };

    loadKaryawanData();
  }, [dispatch]);

  // Effect untuk handle error karyawan
  useEffect(() => {
    if (karyawanErrorMessage && !isKaryawanSelectFetching) {
      toast.error(`Gagal memuat data karyawan: ${karyawanErrorMessage}`, {
        position: "bottom-right",
        autoClose: 5000,
      });
    }
  }, [karyawanErrorMessage, isKaryawanSelectFetching]);

  // Load data absensi dengan pagination
  const loadAbsensiData = useCallback(
    (page = 1, isLoadMore = false) => {
      const params = {
        nik: selectedKaryawan,
        start_date: toLocalDateParam(dateRange.startDate),
        end_date: toLocalDateParam(dateRange.endDate),
        kategori: kategoriFilter,
        page: page,
        page_size: PAGE_SIZE,
      };

      console.log("📊 Loading absensi data:", { page, isLoadMore, params });

      if (isLoadMore) {
        dispatch(loadMoreAbsensi(params));
      } else {
        dispatch(getAbsensiTransaksi(params))
          .unwrap()
          .then(() => {
            setIsInitialLoad(false);
          })
          .catch(() => {
            setIsInitialLoad(false);
          });
      }
    },
    [dispatch, selectedKaryawan, dateRange, kategoriFilter, PAGE_SIZE]
  );

  // Load summary absensi
  const loadAbsensiSummary = useCallback(() => {
    const params = {
      nik: selectedKaryawan,
      start_date: toLocalDateParam(dateRange.startDate),
      end_date: toLocalDateParam(dateRange.endDate),
    };

    dispatch(getAbsensiSummary(params));
  }, [dispatch, selectedKaryawan, dateRange]);

  // Load data saat component mount
  useEffect(() => {
    console.log("🎯 Initial load data...");
    dispatch(clearAbsensiData());
    loadAbsensiData(1, false);
    loadAbsensiSummary();
  }, [dispatch, loadAbsensiData, loadAbsensiSummary]);

  // Handle filter perubahan - reset ke page 1
  useEffect(() => {
    if (!isInitialLoad) {
      console.log("🔄 Filter changed, reloading data...");
      const timer = setTimeout(() => {
        dispatch(resetPagination());
        loadAbsensiData(1, false);
        loadAbsensiSummary();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [
    selectedKaryawan,
    dateRange.startDate,
    dateRange.endDate,
    kategoriFilter,
    isInitialLoad,
    dispatch,
    loadAbsensiData,
    loadAbsensiSummary,
  ]);

  // Klik kartu Rekap Kehadiran — kalau kategori yg sama diklik lagi,
  // toggle-off (kembali ke "tampilkan semua") daripada nge-lock user.
  const handleKategoriClick = (kategori) => {
    setKategoriFilter((prev) => (prev === kategori ? "" : kategori));
  };

  // Navigation handlers
  const handleLoadMore = () => {
    if (hasMoreData && !isLoadMoreFetching) {
      const nextPage = pagination.currentPage + 1;
      console.log("⬇️ Loading more data, page:", nextPage);
      loadAbsensiData(nextPage, true);
    }
  };

  const handlePreviousPage = () => {
    if (pagination.currentPage > 1 && !isGetFetching) {
      const prevPage = pagination.currentPage - 1;
      console.log("⬅️ Going to previous page:", prevPage);
      dispatch(clearAbsensiData());
      loadAbsensiData(prevPage, false);
    }
  };

  const handleNextPage = () => {
    if (hasMoreData && !isGetFetching) {
      const nextPage = pagination.currentPage + 1;
      console.log("➡️ Going to next page:", nextPage);
      dispatch(clearAbsensiData());
      loadAbsensiData(nextPage, false);
    }
  };

  const handleGoToPage = (pageNumber) => {
    if (
      pageNumber >= 1 &&
      pageNumber <= pagination.totalPages &&
      !isGetFetching
    ) {
      console.log("🔢 Going to page:", pageNumber);
      dispatch(clearAbsensiData());
      loadAbsensiData(pageNumber, false);
    }
  };

  const handleRefresh = () => {
    console.log("🔄 Refreshing data...");
    dispatch(clearAbsensiData());
    loadAbsensiData(1, false);
    loadAbsensiSummary();
  };

  // Karyawan change handler — react-select mengirim objek option (atau null
  // saat di-clear), bukan event seperti <select> biasa.
  const handleKaryawanChange = (option) => {
    setSelectedKaryawan(option ? option.value : "");
  };

  const karyawanSelectOptions = useMemo(
    () => [{ value: "", label: "Semua Karyawan" }, ...karyawanOptions],
    [karyawanOptions]
  );
  const selectedKaryawanOption =
    karyawanSelectOptions.find((o) => o.value === selectedKaryawan) || null;

  // File upload handlers
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    dispatch(clearAbsensiMessage());

    if (!file) {
      setExcelFile(null);
      return;
    }

    // Validasi file type
    const validExtensions = [".xlsx", ".xls"];
    const fileExtension = file.name
      .toLowerCase()
      .slice(file.name.lastIndexOf("."));

    if (!validExtensions.includes(fileExtension)) {
      toast.error("Format file tidak didukung. Harus .xlsx atau .xls", {
        position: "bottom-right",
        autoClose: 5000,
      });
      setExcelFile(null);
      return;
    }

    // Validasi file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File terlalu besar. Maksimal 5MB", {
        position: "bottom-right",
        autoClose: 5000,
      });
      setExcelFile(null);
      return;
    }

    setExcelFile(file);
  };

  const handleUploadExcel = async () => {
    if (!excelFile) {
      toast.error("Pilih file terlebih dahulu", {
        position: "bottom-right",
        autoClose: 5000,
      });
      return;
    }

    try {
      const result = await dispatch(importAbsensiTransaksi(excelFile)).unwrap();
      toast.success(result?.message || "File berhasil diupload", {
        position: "bottom-right",
        autoClose: 3000,
      });

      setShowModalUpload(false);
      setExcelFile(null);
      handleRefresh();
    } catch (error) {
      console.error("💥 Upload error:", error);
      toast.error(error?.message || "Gagal upload file", {
        position: "bottom-right",
        autoClose: 5000,
      });
    }
  };

  // Update handleDownloadTemplate function
  const handleDownloadTemplate = () => {
    try {
      // URL ke file template yang sudah ada di public folder
      const templateUrl = "/templates/template_import_absensi.xlsx";

      // Create download link
      const link = document.createElement("a");
      link.href = templateUrl;
      link.download = "template_import_absensi.xlsx";
      link.target = "_blank";

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Template Excel berhasil didownload", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Gagal download template", {
        position: "bottom-right",
        autoClose: 5000,
      });
    }
  };

  // Format data dari API.
  //
  // Terlambat/Cepat Pulang kini dihitung backend (lihat fitur Jadwal Kerja,
  // /human-resource/transaksi/jadwal-kerja) memakai baseline dari
  // "TJadwalKerja" per Pin×Tanggal. Kalau karyawan itu belum punya baris
  // jadwal untuk tanggal ini (`AdaJadwal=false`), kolomnya tampil "-" —
  // bukan 0 — supaya "tepat waktu" tak tertukar dengan "belum ada baseline".
  const formatTableData = useCallback((apiData) => {
    if (!apiData || !Array.isArray(apiData)) {
      return [];
    }

    return apiData.map((item, index) => {
      // Total jam kerja = selisih scan pulang - scan masuk.
      const calculateTotalWorkingHours = (actualIn, actualOut) => {
        if (!actualIn || !actualOut || actualIn === "-" || actualOut === "-") {
          return "0:00";
        }

        try {
          const [inHours, inMinutes] = actualIn.split(":").map(Number);
          const [outHours, outMinutes] = actualOut.split(":").map(Number);

          const startTime = new Date(0, 0, 0, inHours, inMinutes);
          let endTime = new Date(0, 0, 0, outHours, outMinutes);

          // Jika waktu pulang lebih kecil dari waktu masuk, berarti melewati tengah malam
          if (endTime < startTime) {
            endTime.setDate(endTime.getDate() + 1);
          }

          const diffMs = endTime - startTime;
          const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
          const diffMinutes = Math.floor(
            (diffMs % (1000 * 60 * 60)) / (1000 * 60)
          );

          return `${diffHours}:${diffMinutes.toString().padStart(2, "0")}`;
        } catch (error) {
          console.error("Error calculating total hours:", error);
          return "0:00";
        }
      };

      const actualIn = item.ScanMasuk;
      const actualOut = item.ScanPulang;

      const adaMasuk = Boolean(actualIn) && actualIn !== "-";
      const adaPulang = Boolean(actualOut) && actualOut !== "-";
      const adaJadwal = Boolean(item.AdaJadwal);
      const libur = Boolean(item.Libur);
      const adaCuti = Boolean(item.AdaCuti);
      const adaSakit = Boolean(item.AdaSakit);
      const adaIzin = Boolean(item.AdaIzin);

      // Status per hari. Cuti/Sakit/Izin (dari TCuti/TSakit/TIzin approved,
      // lihat modul Izin/Cuti/Sakit) DIDAHULUKAN atas scan mesin — keputusan
      // sengaja: dokumen resmi lebih dipercaya daripada bekas scan kalau
      // kebetulan tumpang tindih (mis. cuti setengah hari lalu sempat masuk).
      // "Tidak Ada Scan Masuk" masih mungkin terjadi pada pekerja shift malam:
      // scan pulangnya jatuh di tanggal berikutnya, jadi hari ini hanya
      // menyisakan satu sisi. "Libur" (jadwal eksplisit hari off) didahulukan
      // atas itu semua — beda arti dari "Tanpa Keterangan" yang berarti mangkir
      // tanpa dokumen apa pun.
      let statusMessage;
      if (libur) {
        statusMessage = "Libur";
      } else if (adaCuti) {
        statusMessage = "Cuti";
      } else if (adaSakit) {
        statusMessage = "Sakit";
      } else if (adaIzin) {
        statusMessage = "Izin";
      } else if (!adaMasuk && !adaPulang) {
        statusMessage = "Tanpa Keterangan";
      } else if (!adaMasuk) {
        statusMessage = "Tidak Ada Scan Masuk";
      } else if (!adaPulang) {
        statusMessage = "Tidak Ada Scan Pulang";
      } else {
        statusMessage = "Hadir";
      }

      const terlambatMenit = Number(item.Terlambat || 0);
      const cepatPulangMenit = Number(item.CepatPulang || 0);
      const lemburMenit = Number(item.Lembur || 0);
      const scanLebihAwalMenit = Number(item.ScanLebihAwal || 0);

      return {
        // Kunci baris = PIN karyawan. Baris kini digerakkan oleh MKaryawan, satu
        // baris per karyawan per hari, jadi PIN sudah unik dalam satu halaman —
        // dan tak lagi ikut berubah tiap render seperti `Date.now()` dulu.
        id: item.Nik || `row-${index}`,
        date: item.TanggalDisplay || item.Tanggal || "-",
        inAct: adaMasuk ? actualIn : "-",
        outAct: adaPulang ? actualOut : "-",
        hari: adaMasuk || adaPulang ? "1" : "0",
        totalJam:
          adaMasuk && adaPulang
            ? calculateTotalWorkingHours(actualIn, actualOut)
            : "0:00",
        namaKaryawan: item.NamaKaryawan || "-",
        nik: item.Nik || "-",
        departemen: item.Departemen || "-",
        status: statusMessage,
        adaJadwal,
        libur,
        adaCuti,
        adaSakit,
        adaIzin,
        terlambat:
          !adaJadwal || libur
            ? "-"
            : terlambatMenit > 0
            ? `${terlambatMenit} mnt`
            : "Tepat Waktu",
        cepatPulang:
          !adaJadwal || libur
            ? "-"
            : cepatPulangMenit > 0
            ? `${cepatPulangMenit} mnt`
            : "-",
        lembur:
          !adaJadwal || libur
            ? "-"
            : lemburMenit > 0
            ? `${lemburMenit} mnt`
            : "-",
        scanLebihAwal:
          !adaJadwal || libur
            ? "-"
            : scanLebihAwalMenit > 0
            ? `${scanLebihAwalMenit} mnt`
            : "-",
      };
    });
  }, []);

  // Statistik kehadiran hari itu.
  //
  // Sumbernya RINGKASAN dari server, bukan `tableData`. Tabelnya dipaginasi 50
  // baris sementara karyawan aktif ada 155 — grafik yang dihitung dari baris
  // yang kebetulan tampil dulu hanya menggambarkan sepertiga orang dan
  // angkanya berubah-ubah tiap kali pindah halaman.
  const calculateChartData = useCallback((summary) => {
    const total = Number(summary?.total_karyawan ?? 0);
    if (!total) {
      return { kehadiran: null };
    }

    const tidakMasuk = Number(summary?.tidak_masuk ?? 0);
    const tanpaScanPulang = Number(summary?.tanpa_scan_pulang ?? 0);
    // Kategori tersendiri, bukan digabung ke "tidak masuk": ini orang yang
    // terekam mesin hari itu — umumnya shift malam yang scan pulangnya saja
    // jatuh di tanggal ini — jadi menghitungnya sebagai absen itu keliru.
    const tanpaScanMasuk = Number(summary?.tanpa_scan_masuk ?? 0);
    const hadirLengkap = Number(summary?.hadir_lengkap ?? 0);
    // Keterlambatan hanya bisa dinilai untuk karyawan yang punya baris jadwal
    // hari itu — `berjadwal` dibawa serta supaya "Terlambat 0" tidak terbaca
    // "semua tepat waktu" padahal bisa berarti "jadwalnya belum diunggah".
    const terlambat = Number(summary?.terlambat ?? 0);
    const berjadwal = Number(summary?.berjadwal ?? 0);
    const lembur = Number(summary?.lembur ?? 0);
    const scanLebihAwal = Number(summary?.scan_lebih_awal ?? 0);
    // Cuti/Sakit/Izin (dihubungkan dari TCuti/TSakit/TIzin approved) dan
    // Tanpa Keterangan — pecahan dari "tidak_masuk" lama, lihat backend
    // GetAbsensiSummary. hadirBersih = hadir TANPA yang overlap dokumen cuti/
    // sakit/izin (dokumen menang, lihat komentar di formatTableData).
    const cuti = Number(summary?.cuti ?? 0);
    const sakit = Number(summary?.sakit ?? 0);
    const izin = Number(summary?.izin ?? 0);
    const hadirBersih = Number(summary?.hadir_bersih ?? 0);
    const tanpaKeterangan = Number(summary?.tanpa_keterangan ?? 0);

    return {
      kehadiran: {
        labels: [
          "Hadir Lengkap",
          "Tanpa Scan Pulang",
          "Tanpa Scan Masuk",
          "Tidak Masuk",
        ],
        datasets: [
          {
            label: "Jumlah Karyawan",
            data: [hadirLengkap, tanpaScanPulang, tanpaScanMasuk, tidakMasuk],
            backgroundColor: ["#10B981", "#F59E0B", "#6366F1", "#EF4444"],
            borderColor: ["#059669", "#B45309", "#4338CA", "#DC2626"],
            borderWidth: 1,
          },
        ],
      },
      totalKaryawan: total,
      hadirLengkap,
      tanpaScanPulang,
      tanpaScanMasuk,
      tidakMasuk,
      terlambat,
      berjadwal,
      lembur,
      scanLebihAwal,
      cuti,
      sakit,
      izin,
      hadirBersih,
      tanpaKeterangan,
    };
  }, []);

  // Urutan baris = Scan Masuk ascending, PERSIS seperti ORDER BY di
  // GetAbsensiPagination — jangan diurutkan ulang di sini. Backend adalah
  // satu-satunya sumber urutan (tabelnya dipaginasi 50 baris, menyusun ulang
  // di klien cuma menata halaman yang kebetulan termuat).
  const tableData = useMemo(() => formatTableData(absensiData), [absensiData, formatTableData]);
  const summaryData = absensiSummary;
  const chartData = useMemo(
    () => calculateChartData(summaryData),
    [summaryData, calculateChartData]
  );

  const handleCloseModal = () => {
    setShowModalUpload(false);
    setExcelFile(null);
    dispatch(clearAbsensiMessage());
  };

  // Komponen Pagination
  const PaginationInfo = () => {
    const startItem = (pagination.currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(
      pagination.currentPage * PAGE_SIZE,
      pagination.totalCount
    );

    const generatePageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;

      let startPage = Math.max(
        1,
        pagination.currentPage - Math.floor(maxVisiblePages / 2)
      );
      let endPage = Math.min(
        pagination.totalPages,
        startPage + maxVisiblePages - 1
      );

      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      return pages;
    };

    const pageNumbers = generatePageNumbers();

    return (
      <div className="flex flex-col sm:flex-row justify-between items-center mt-4 px-4 py-3 bg-blueGray-50 rounded gap-3">
        <div className="text-sm text-blueGray-600">
          Menampilkan{" "}
          <strong>
            {startItem}-{endItem}
          </strong>{" "}
          dari <strong>{pagination.totalCount}</strong> record
        </div>

        <div className="flex items-center gap-1">
          <Button
            color="lightBlue"
            buttonType="outline"
            size="sm"
            onClick={() => handleGoToPage(1)}
            disabled={pagination.currentPage <= 1 || isGetFetching}
          >
            ⏮
          </Button>

          <Button
            color="lightBlue"
            buttonType="outline"
            size="sm"
            onClick={handlePreviousPage}
            disabled={pagination.currentPage <= 1 || isGetFetching}
          >
            ←
          </Button>

          {pageNumbers.map((page) => (
            <Button
              key={page}
              color={pagination.currentPage === page ? "lightBlue" : "white"}
              buttonType={
                pagination.currentPage === page ? "filled" : "outline"
              }
              size="sm"
              onClick={() => handleGoToPage(page)}
              disabled={isGetFetching}
            >
              {page}
            </Button>
          ))}

          <Button
            color="lightBlue"
            buttonType="outline"
            size="sm"
            onClick={handleNextPage}
            disabled={!hasMoreData || isGetFetching}
          >
            →
          </Button>

          <Button
            color="lightBlue"
            buttonType="outline"
            size="sm"
            onClick={() => handleGoToPage(pagination.totalPages)}
            disabled={!hasMoreData || isGetFetching}
          >
            ⏭
          </Button>
        </div>
      </div>
    );
  };

  // Komponen Loading Indicator
  const LoadingIndicator = ({ type = "initial" }) => (
    <div className="flex justify-center items-center my-4 py-4">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      <span className="ml-3 text-blueGray-600">
        {type === "initial"
          ? "Memuat data absensi..."
          : "Memuat data tambahan..."}
      </span>
    </div>
  );

  // Tampilkan loading saat initial fetching data utama
  if (isGetFetching && isInitialLoad) {
    return <Loading />;
  }

  return (
    <>
      <div className="md:ml-64">
        <div
          style={{ background: Colors.primary }}
          className="pt-14 pb-16 px-3 md:px-8 h-auto"
        >
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"></div>
          </div>
        </div>

        <div className="px-3 md:px-8 h-auto -mt-24">
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 px-4">
              {/* Header dengan Filter dan Tombol */}
              <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded">
                <div className="rounded-t mb-0 px-4 py-3 border-0">
                  <div className="flex flex-wrap items-center">
                    <div className="relative w-full px-4 max-w-full flex-grow flex-1">
                      <h3 className="font-semibold text-lg text-blueGray-700">
                        Laporan Absensi - {selectedKaryawan || "Semua Karyawan"}
                        {kategoriFilter && (
                          <span className="ml-2 text-sm font-normal text-blueGray-500">
                            (filter: {KATEGORI_LABEL[kategoriFilter] || kategoriFilter})
                          </span>
                        )}
                      </h3>
                      <div className="flex flex-wrap gap-4 mt-2">
                        {/* Rentang tanggal (dari - sampai). Satu baris per karyawan aktif
                            per hari; klik satu tanggal lalu tanggal kedua. Bila baru
                            klik tanggal pertama, rentang sementara = satu hari itu. */}
                        <div className="flex flex-col">
                          <label className="text-xs text-blueGray-400 mb-1">
                            Rentang Tanggal
                          </label>
                          <DatePicker
                            selectsRange
                            startDate={dateRange.startDate}
                            endDate={dateRange.pickingEnd ? null : dateRange.endDate}
                            onChange={(update) => {
                              const [start, end] = update || [];
                              if (!start) return;
                              if (!end) {
                                setDateRange({ startDate: start, endDate: start, pickingEnd: true });
                              } else {
                                setDateRange({ startDate: start, endDate: end });
                              }
                            }}
                            className="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full"
                            dateFormat="dd/MM/yyyy"
                            maxDate={new Date()}
                            placeholderText="Pilih rentang tanggal"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-4 lg:mt-0">
                      {/* Filter Nama Karyawan — select-search (react-select),
                          bukan <select> biasa: daftar karyawan aktif bisa
                          ratusan baris, sulit dicari lewat scroll dropdown polos. */}
                      <div className="relative flex flex-col min-w-[220px]">
                        <label className="text-xs text-blueGray-400 mb-1">
                          Karyawan {isKaryawanSelectFetching && "(Loading...)"}
                        </label>
                        <Select
                          value={selectedKaryawanOption}
                          onChange={handleKaryawanChange}
                          options={karyawanSelectOptions}
                          isDisabled={isKaryawanSelectFetching}
                          isLoading={isKaryawanSelectFetching}
                          isClearable
                          placeholder={
                            isKaryawanSelectError
                              ? "Gagal memuat data"
                              : "Cari karyawan..."
                          }
                          noOptionsMessage={() => "Data tidak tersedia"}
                          className="text-sm"
                          classNamePrefix="karyawan-select"
                        />
                      </div>

                      {/* Tombol Upload Excel */}
                      <div className="flex flex-col justify-end">
                        <Button
                          color="lightGreen"
                          buttonType="filled"
                          size="regular"
                          rounded={false}
                          block={false}
                          iconOnly={false}
                          ripple="light"
                          onClick={() => setShowModalUpload(true)}
                          disabled={isImportFetching}
                        >
                          {isImportFetching ? "Uploading..." : "Upload Excel"}
                        </Button>
                      </div>

                      {/* Tombol Refresh */}
                      <div className="flex flex-col justify-end">
                        <Button
                          color="lightBlue"
                          buttonType="filled"
                          size="regular"
                          rounded={false}
                          block={false}
                          iconOnly={false}
                          ripple="light"
                          onClick={handleRefresh}
                          disabled={isGetFetching}
                        >
                          {isGetFetching ? "Loading..." : "Refresh"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section Chart — satu grafik kehadiran, dihitung dari
                  ringkasan server (seluruh karyawan aktif), bukan dari baris
                  halaman berjalan. Grafik keterlambatan/kepulangan yang lama
                  dilepas: keduanya butuh jadwal kerja, dan master jadwal itu
                  tidak ada di database. */}
              {chartData.kehadiran && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <div className="relative flex flex-col min-w-0 break-words bg-white w-full shadow-lg rounded">
                    <div className="rounded-t mb-0 px-4 py-3 border-0 bg-blueGray-100">
                      <div className="flex flex-wrap items-center">
                        <div className="relative w-full px-4 max-w-full flex-grow flex-1">
                          <h3 className="font-semibold text-base text-blueGray-700">
                            Kehadiran Hari Ini
                          </h3>
                        </div>
                      </div>
                    </div>
                    <div className="block w-full overflow-x-auto p-4">
                      <div className="h-80">
                        <Doughnut
                          data={chartData.kehadiran}
                          options={doughnutChartOptions}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="relative flex flex-col min-w-0 break-words bg-white w-full shadow-lg rounded">
                    <div className="rounded-t mb-0 px-4 py-3 border-0 bg-blueGray-100">
                      <div className="flex flex-wrap items-center justify-between">
                        <h3 className="font-semibold text-base text-blueGray-700">
                          Rekap Kehadiran
                        </h3>
                        {kategoriFilter && (
                          <button
                            onClick={() => setKategoriFilter("")}
                            className="text-xs text-blueGray-500 hover:text-blueGray-700 underline"
                          >
                            × Hapus filter
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="block w-full overflow-x-auto">
                      <table className="items-center w-full bg-transparent border-collapse">
                        <tbody>
                          {/* Klik baris manapun (kecuali Karyawan Aktif/Total Jam
                              Kerja) untuk menyaring tabel di bawah ke kategori itu
                              saja — klik lagi utk kembali ke "tampilkan semua".
                              rowCls() menandai baris yg sedang aktif dipilih. */}
                          <tr
                            className="border-b border-blueGray-200 cursor-pointer hover:bg-blueGray-50"
                            onClick={() => setKategoriFilter("")}
                            title="Tampilkan semua karyawan"
                          >
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                              Karyawan Aktif
                            </td>
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right font-semibold">
                              {chartData.totalKaryawan} orang
                            </td>
                          </tr>
                          {/* Hadir = ada scan mesin DAN tak punya dokumen Cuti/
                              Sakit/Izin approved yang menutupi hari ini (dokumen
                              menang atas scan kalau tumpang tindih — lihat
                              formatTableData). Beda dari "hadir" mentah di baris-
                              baris breakdown bawah, yang tak memperhitungkan itu. */}
                          <tr
                            className={`border-b border-blueGray-200 cursor-pointer hover:brightness-95 ${
                              kategoriFilter === "hadir_bersih" ? "ring-2 ring-inset ring-green-500" : ""
                            } bg-green-50`}
                            onClick={() => handleKategoriClick("hadir_bersih")}
                          >
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 font-semibold">
                              Hadir
                            </td>
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right font-bold text-green-600">
                              {chartData.hadirBersih} orang
                            </td>
                          </tr>
                          <tr
                            className={`border-b border-blueGray-200 cursor-pointer hover:bg-blueGray-50 ${
                              kategoriFilter === "hadir_lengkap" ? "ring-2 ring-inset ring-green-500" : ""
                            }`}
                            onClick={() => handleKategoriClick("hadir_lengkap")}
                          >
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                              Hadir Lengkap
                            </td>
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right font-semibold text-green-600">
                              {chartData.hadirLengkap} orang
                            </td>
                          </tr>
                          <tr
                            className={`border-b border-blueGray-200 cursor-pointer hover:bg-blueGray-50 ${
                              kategoriFilter === "tanpa_scan_pulang" ? "ring-2 ring-inset ring-orange-500" : ""
                            }`}
                            onClick={() => handleKategoriClick("tanpa_scan_pulang")}
                          >
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                              Tanpa Scan Pulang
                            </td>
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right font-semibold text-orange-600">
                              {chartData.tanpaScanPulang} orang
                            </td>
                          </tr>
                          {/* Cuti/Sakit/Izin — dihubungkan dari TCuti/TSakit/TIzin
                              (Status approved), menggantikan kotak "Tidak Masuk"
                              lama yang menggabung semuanya jadi satu angka buram. */}
                          <tr
                            className={`border-b border-blueGray-200 cursor-pointer hover:bg-blueGray-50 ${
                              kategoriFilter === "cuti" ? "ring-2 ring-inset ring-blue-500" : ""
                            }`}
                            onClick={() => handleKategoriClick("cuti")}
                          >
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                              Cuti
                            </td>
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right font-semibold text-blue-600">
                              {chartData.cuti} orang
                            </td>
                          </tr>
                          <tr
                            className={`border-b border-blueGray-200 cursor-pointer hover:bg-blueGray-50 ${
                              kategoriFilter === "sakit" ? "ring-2 ring-inset ring-pink-500" : ""
                            }`}
                            onClick={() => handleKategoriClick("sakit")}
                          >
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                              Sakit
                            </td>
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right font-semibold text-pink-600">
                              {chartData.sakit} orang
                            </td>
                          </tr>
                          <tr
                            className={`border-b border-blueGray-200 cursor-pointer hover:bg-blueGray-50 ${
                              kategoriFilter === "izin" ? "ring-2 ring-inset ring-teal-500" : ""
                            }`}
                            onClick={() => handleKategoriClick("izin")}
                          >
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                              Izin
                            </td>
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right font-semibold text-teal-600">
                              {chartData.izin} orang
                            </td>
                          </tr>
                          <tr
                            className={`border-b border-blueGray-200 cursor-pointer hover:bg-blueGray-50 ${
                              kategoriFilter === "tanpa_keterangan" ? "ring-2 ring-inset ring-red-500" : ""
                            }`}
                            onClick={() => handleKategoriClick("tanpa_keterangan")}
                          >
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs p-4">
                              Tanpa Keterangan
                              <span className="block text-blueGray-400 normal-case">
                                tak ada scan & tak ada dokumen Cuti/Sakit/Izin
                              </span>
                            </td>
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right font-semibold text-red-600">
                              {chartData.tanpaKeterangan} orang
                            </td>
                          </tr>
                          {/* Terlambat dinilai dari baseline "TJadwalKerja"
                              (Setup › Jadwal Kerja). Karyawan tanpa jadwal hari
                              itu tak bisa dinilai, jadi pembaginya ikut
                              ditampilkan — tanpa itu angka 0 menyesatkan. */}
                          <tr
                            className={`border-b border-blueGray-200 ${
                              chartData.berjadwal > 0 ? "cursor-pointer hover:bg-blueGray-50" : "opacity-60"
                            } ${kategoriFilter === "terlambat" ? "ring-2 ring-inset ring-red-500" : ""}`}
                            onClick={() => chartData.berjadwal > 0 && handleKategoriClick("terlambat")}
                          >
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs p-4">
                              Terlambat
                              <span className="block text-blueGray-400 normal-case">
                                {chartData.berjadwal > 0
                                  ? `dari ${chartData.berjadwal} karyawan berjadwal`
                                  : "belum bisa dinilai — jadwal kerja hari ini belum diunggah"}
                              </span>
                            </td>
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right font-semibold text-red-600">
                              {chartData.berjadwal > 0
                                ? `${chartData.terlambat} orang`
                                : "-"}
                            </td>
                          </tr>
                          {/* Lembur/Scan Lebih Awal - cermin Terlambat, sisi
                              lain dari toleransi jadwal yg sama. */}
                          <tr
                            className={`border-b border-blueGray-200 ${
                              chartData.berjadwal > 0 ? "cursor-pointer hover:bg-blueGray-50" : "opacity-60"
                            } ${kategoriFilter === "lembur" ? "ring-2 ring-inset ring-purple-500" : ""}`}
                            onClick={() => chartData.berjadwal > 0 && handleKategoriClick("lembur")}
                          >
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs p-4">
                              Lembur
                              <span className="block text-blueGray-400 normal-case">
                                {chartData.berjadwal > 0
                                  ? `dari ${chartData.berjadwal} karyawan berjadwal`
                                  : "belum bisa dinilai — jadwal kerja hari ini belum diunggah"}
                              </span>
                            </td>
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right font-semibold text-purple-600">
                              {chartData.berjadwal > 0 ? `${chartData.lembur} orang` : "-"}
                            </td>
                          </tr>
                          <tr className="bg-green-50">
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 font-bold">
                              Total Jam Kerja
                            </td>
                            <td className="px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right font-bold text-green-600">
                              {summaryData.total_jam_kerja || "0:00"}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Loading indicator untuk initial load */}
              {isGetFetching && !isLoadMoreFetching && (
                <LoadingIndicator type="initial" />
              )}

              {/* Tabel Absensi */}
              <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded overflow-x-auto">
                <table className="items-center bg-transparent w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="px-6 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center">
                        No
                      </th>
                      <th className="px-6 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center">
                        Tanggal
                      </th>
                      <th className="px-6 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center">
                        Nama
                      </th>
                      <th className="px-6 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center">
                        NIK
                      </th>
                      <th className="px-6 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center">
                        Departemen
                      </th>
                      <th className="px-6 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center">
                        Scan Masuk
                      </th>
                      <th className="px-6 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center">
                        Scan Pulang
                      </th>
                      <th className="px-6 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center">
                        Total Jam
                      </th>
                      <th className="px-6 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center">
                        Terlambat
                      </th>
                      <th className="px-6 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center">
                        Cepat Pulang
                      </th>
                      <th className="px-6 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center">
                        Lembur
                      </th>
                      <th className="px-6 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center">
                        Scan Lebih Awal
                      </th>
                      <th className="px-6 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center">
                        Hari
                      </th>
                      <th className="px-6 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.length > 0 ? (
                      tableData.map((row, index) => {
                        const nomorUrut =
                          (pagination.currentPage - 1) * PAGE_SIZE + index + 1;

                        const getStatusColor = (status) => {
                          if (status === "Libur")
                            return "text-blueGray-500 bg-blueGray-50";
                          if (status === "Tanpa Keterangan")
                            return "text-red-600 bg-red-50";
                          if (status === "Cuti")
                            return "text-blue-600 bg-blue-50";
                          if (status === "Sakit")
                            return "text-pink-600 bg-pink-50";
                          if (status === "Izin")
                            return "text-teal-600 bg-teal-50";
                          if (status.startsWith("Tidak Ada Scan"))
                            return "text-orange-600 bg-orange-50";
                          if (status === "Hadir")
                            return "text-green-600 bg-green-50";
                          return "";
                        };

                        const statusClass = getStatusColor(row.status);

                        return (
                          <tr
                            key={row.id}
                            className={`hover:bg-blueGray-50 ${statusClass}`}
                          >
                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-center">
                              {nomorUrut}
                            </td>
                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-center">
                              {row.date}
                            </td>
                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left">
                              {row.namaKaryawan}
                            </td>
                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-center">
                              {row.nik}
                            </td>
                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left">
                              {row.departemen}
                            </td>
                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-center">
                              {row.inAct}
                            </td>
                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-center">
                              {row.outAct}
                            </td>
                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-center">
                              {row.totalJam}
                            </td>
                            <td
                              className={`border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-center ${
                                row.terlambat !== "-" &&
                                row.terlambat !== "Tepat Waktu"
                                  ? "text-red-600 font-semibold"
                                  : ""
                              }`}
                            >
                              {row.terlambat}
                            </td>
                            <td
                              className={`border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-center ${
                                row.cepatPulang !== "-"
                                  ? "text-orange-600 font-semibold"
                                  : ""
                              }`}
                            >
                              {row.cepatPulang}
                            </td>
                            <td
                              className={`border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-center ${
                                row.lembur !== "-"
                                  ? "text-purple-600 font-semibold"
                                  : ""
                              }`}
                            >
                              {row.lembur}
                            </td>
                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-center">
                              {row.scanLebihAwal}
                            </td>
                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-center">
                              {row.hari}
                            </td>
                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-center font-semibold">
                              {row.status}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan="14"
                          className="text-center py-8 text-gray-500"
                        >
                          {isGetFetching
                            ? "Memuat data..."
                            : kategoriFilter
                            ? `Tidak ada karyawan dengan kategori "${KATEGORI_LABEL[kategoriFilter] || kategoriFilter}" pada tanggal ini`
                            : "Tidak ada karyawan aktif untuk tanggal yang dipilih"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Pagination Info */}
                {tableData.length > 0 && <PaginationInfo />}
              </div>

              {/* Loading indicator untuk load more */}
              {isLoadMoreFetching && <LoadingIndicator type="more" />}

              {/* Ringkasan lama dilepas: "Total Hari Kerja" selalu bernilai 1
                  pada halaman per hari, dan "Karyawan Terlambat" tak bisa
                  dihitung tanpa master jadwal. Penggantinya = kartu "Rekap
                  Kehadiran" di atas, yang angkanya datang dari ringkasan server
                  sehingga mencakup seluruh karyawan, bukan satu halaman saja. */}

            </div>
          </div>
        </div>
      </div>

      {/* Modal Upload Excel */}
      <Modal size="regular" active={showModalUpload} toggler={handleCloseModal}>
        <ModalHeader toggler={handleCloseModal}>
          Upload Data Absensi via Excel
        </ModalHeader>
        <ModalBody>
          <div className="w-full max-w-lg">
            {/* Error Message Display */}
            {absensiErrorMessage && (
              <div className="flex flex-wrap -mx-3 mb-4">
                <div className="w-full px-3">
                  <div className="bg-red-50 border border-red-200 rounded p-3">
                    <p className="text-red-800 text-sm font-semibold">
                      Error: {absensiErrorMessage}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {absensiMessage && (
              <div className="flex flex-wrap -mx-3 mb-4">
                <div className="w-full px-3">
                  <div className="bg-green-50 border border-green-200 rounded p-3">
                    <p className="text-green-800 text-sm font-semibold">
                      ✅ {absensiMessage}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap -mx-3 mb-6">
              <div className="w-full px-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                  Pilih File Excel
                </label>
                <input
                  type="file"
                  accept=".xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  onChange={handleFileUpload}
                  className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white border-gray-500"
                  disabled={isImportFetching}
                />
                <p className="text-gray-600 text-xs italic mt-2">
                  Format yang didukung: .xlsx, .xls (Maksimal 5MB)
                </p>
              </div>
            </div>

            {excelFile && (
              <div className="flex flex-wrap -mx-3 mb-6">
                <div className="w-full px-3">
                  <div className="bg-green-50 border border-green-200 rounded p-3">
                    <p className="text-green-800 text-sm">
                      File terpilih: <strong>{excelFile.name}</strong>
                    </p>
                    <p className="text-green-600 text-xs">
                      Size: {(excelFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap -mx-3 mb-6">
              <div className="w-full px-3">
                <Button
                  color="lightBlue"
                  buttonType="filled"
                  size="regular"
                  rounded={false}
                  block={true}
                  iconOnly={false}
                  ripple="light"
                  onClick={handleDownloadTemplate}
                  disabled={isDownloadFetching}
                >
                  {isDownloadFetching
                    ? "Downloading..."
                    : "Download Template Excel"}
                </Button>
                <p className="text-gray-600 text-xs italic mt-2">
                  Download template untuk format data yang benar
                </p>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="red"
            buttonType="link"
            onClick={handleCloseModal}
            ripple="dark"
            disabled={isImportFetching}
          >
            Cancel
          </Button>

          <Button
            color="green"
            onClick={handleUploadExcel}
            disabled={!excelFile || isImportFetching}
            ripple="light"
          >
            {isImportFetching ? "Uploading..." : "Upload Excel"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default AbsensiPage;
